import { useState } from 'react'
import { QuestionMarkCircleIcon } from '@heroicons/react/24/outline'
import SimpleModal from '../../../components/SimpleModal'

/**
 * The thresholds are the one part of a classifier version whose effect isn't
 * visible from the form — they only bite at go-live, when they become the
 * account's SUSPECT and SEVERE moderation boundaries. Admins also need the
 * above-1 trick (EN-510) to run a classifier for signal alone, and that is
 * discoverable nowhere else, so it lives behind this button rather than in a
 * hint line nobody has room to read.
 */
export default function ThresholdHelp({ className = '' }: { className?: string }) {
    const [open, setOpen] = useState(false)

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                aria-label="How thresholds work"
                title="How thresholds work"
                className={`rounded-full text-ink-400 hover:text-arwen-600 focus:outline-none focus:ring-1 focus:ring-arwen-500 ${className}`}
            >
                <QuestionMarkCircleIcon className="h-4 w-4" aria-hidden="true" />
            </button>

            <SimpleModal open={open} onClose={() => setOpen(false)}>
                <div className="w-[560px] max-w-[94vw] overflow-hidden rounded-xl bg-white text-left text-sm text-ink-800 shadow-2xl">
                    <div className="border-b border-ink-100 px-5 py-4">
                        <h3 className="m-0 font-sans text-lg font-medium text-ink-900">
                            How thresholds work
                        </h3>
                        <p className="mt-1 mb-0 text-xs text-ink-500">
                            Every comment is scored 0–1 by this classifier. The two thresholds
                            decide what that score does.
                        </p>
                    </div>

                    <div className="space-y-4 px-5 py-4">
                        <div>
                            <h4 className="m-0 mb-2 text-[11px] font-semibold uppercase tracking-wide text-ink-500">
                                The two boundaries
                            </h4>
                            <dl className="m-0 space-y-2">
                                <div className="flex gap-2.5">
                                    <span
                                        className="mt-1.5 h-2 w-2 flex-none rounded-full bg-suspect"
                                        aria-hidden="true"
                                    />
                                    <div>
                                        <dt className="inline font-semibold text-ink-900">
                                            Borderline
                                        </dt>
                                        <dd className="m-0 inline text-ink-700">
                                            {' '}
                                            — the score at which a comment becomes{' '}
                                            <strong>Suspect</strong>. It goes to the moderation
                                            queue for a human to judge.
                                        </dd>
                                    </div>
                                </div>
                                <div className="flex gap-2.5">
                                    <span
                                        className="mt-1.5 h-2 w-2 flex-none rounded-full bg-severe"
                                        aria-hidden="true"
                                    />
                                    <div>
                                        <dt className="inline font-semibold text-ink-900">
                                            Confirmed
                                        </dt>
                                        <dd className="m-0 inline text-ink-700">
                                            {' '}
                                            — the score at which a comment becomes{' '}
                                            <strong>Severe</strong> and is acted on automatically,
                                            where the profile has auto-moderation on.
                                        </dd>
                                    </div>
                                </div>
                                <div className="flex gap-2.5">
                                    <span
                                        className="mt-1.5 h-2 w-2 flex-none rounded-full bg-safe"
                                        aria-hidden="true"
                                    />
                                    <div>
                                        <dt className="inline font-semibold text-ink-900">Safe</dt>
                                        <dd className="m-0 inline text-ink-700">
                                            {' '}
                                            — anything below Borderline. There is no Safe threshold
                                            to set; it is simply what is left.
                                        </dd>
                                    </div>
                                </div>
                            </dl>
                        </div>

                        <div>
                            <h4 className="m-0 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-500">
                                The rules
                            </h4>
                            <ul className="m-0 list-disc space-y-1 pl-5 text-ink-700">
                                <li>Confirmed must be greater than Borderline.</li>
                                <li>
                                    Either may be left empty — a version can define any combination
                                    of bands.
                                </li>
                                <li>
                                    They take effect when the version goes live and the classifier
                                    is deployed, as the SUSPECT and SEVERE boundary rows on each
                                    profile.
                                </li>
                            </ul>
                        </div>

                        <div className="rounded-lg border border-arwen-200 bg-arwen-50 px-4 py-3">
                            <h4 className="m-0 mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-arwen-700">
                                Classifying without moderating
                            </h4>
                            <p className="m-0 mb-2 text-ink-700">
                                A classifier that should classify but never hide — Buyer Intent, for
                                example — needs thresholds no score can reach, or thresholds set to
                                None. So two ways of doing this:
                            </p>
                            <ul className="m-0 list-disc space-y-1 pl-5 text-ink-700">
                                <li>
                                    Classification scores never exceed 1, so set Borderline to{' '}
                                    <span className="tabular-nums font-semibold">1.01</span> and
                                    Confirmed to{' '}
                                    <span className="tabular-nums font-semibold">1.02</span>; or
                                </li>
                                <li>Delete the scores so that the box reads None</li>
                            </ul>
                        </div>
                    </div>

                    <div className="flex justify-end border-t border-ink-100 bg-ink-50 px-5 py-3.5">
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </SimpleModal>
        </>
    )
}
